import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, Upload, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicGalleryUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState(localStorage.getItem("gallery_uploader_name") || "");
  const [albumId, setAlbumId] = useState("");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/gallery/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setError("Este link no es válido o fue desactivado. Pide uno nuevo al residente."));
  }, [token]);

  const pick = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 20);
    setFiles(list);
    setPreviews(list.map((f) => URL.createObjectURL(f)));
  };

  const send = async () => {
    if (!name.trim()) { alert("Escribe tu nombre"); return; }
    if (files.length === 0) { alert("Selecciona o toma fotos primero"); return; }
    setUploading(true);
    try {
      localStorage.setItem("gallery_uploader_name", name.trim());
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("uploader_name", name.trim());
      if (albumId) fd.append("album_id", albumId);
      if (caption.trim()) fd.append("caption", caption.trim());
      const { data } = await axios.post(`${API}/public/gallery/${token}/photos`, fd, { timeout: 300000 });
      setDone(data.uploaded);
      setFiles([]); setPreviews([]); setCaption("");
    } catch (e) {
      alert(e.response?.data?.detail || "Error al subir. Revisa tu conexión e intenta de nuevo.");
    }
    setUploading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#05070f" }}>
        <div className="text-center max-w-sm">
          <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: "#ef4444" }} />
          <div className="text-slate-200 font-semibold">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ background: "#05070f" }} data-testid="public-gallery-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6 pt-4">
          <Camera className="w-10 h-10 mx-auto mb-3" style={{ color: "#22d3ee" }} />
          <h1 className="text-xl font-bold text-slate-100">Fotos de obra</h1>
          <div className="text-sm mt-1" style={{ color: "#22d3ee" }} data-testid="public-gallery-project">
            {info ? `${info.project_code ? info.project_code + " — " : ""}${info.project_name}` : "Cargando…"}
          </div>
          <div className="text-xs text-slate-500 mt-1">Sube las fotos del avance directo desde tu celular.</div>
        </div>

        {done > 0 && (
          <div className="rounded-md p-4 mb-4 flex items-center gap-3" style={{ background: "#22c55e14", border: "1px solid #22c55e55" }} data-testid="public-upload-success">
            <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: "#22c55e" }} />
            <div className="text-sm text-slate-200">¡{done} foto(s) enviada(s)! Puedes subir más si quieres.</div>
          </div>
        )}

        <div className="rounded-md p-4 space-y-4" style={{ background: "#0b1020", border: "1px solid #1e2a45" }}>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Tu nombre *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Juan Pérez (herrero)" data-testid="public-name-input"
              className="w-full rounded-sm px-3 py-2.5 text-sm text-slate-100" style={{ background: "#05070f", border: "1px solid #1e2a45" }} />
          </div>
          {info?.albums?.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Álbum (opcional)</label>
              <select value={albumId} onChange={(e) => setAlbumId(e.target.value)} data-testid="public-album-select"
                className="w-full rounded-sm px-3 py-2.5 text-sm text-slate-100" style={{ background: "#05070f", border: "1px solid #1e2a45" }}>
                <option value="">— Sin álbum —</option>
                {info.albums.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Nota (opcional)</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ej. Colado de losa nivel 2" data-testid="public-caption-input"
              className="w-full rounded-sm px-3 py-2.5 text-sm text-slate-100" style={{ background: "#05070f", border: "1px solid #1e2a45" }} />
          </div>
          <button onClick={() => fileRef.current?.click()} data-testid="public-pick-btn"
            className="w-full rounded-sm px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2"
            style={{ border: "1px dashed #22d3ee88", color: "#22d3ee", background: "#22d3ee0d" }}>
            <Camera className="w-4 h-4" /> {files.length ? `${files.length} foto(s) seleccionada(s)` : "Tomar / elegir fotos"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={pick} data-testid="public-file-input" />
          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-1.5">
              {previews.map((src, i) => <img key={i} src={src} alt="" className="aspect-square object-cover rounded-sm" />)}
            </div>
          )}
          <button onClick={send} disabled={uploading || files.length === 0} data-testid="public-upload-btn"
            className="w-full rounded-sm px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: "#22d3ee", color: "#05070f" }}>
            <Upload className="w-4 h-4" /> {uploading ? "Subiendo fotos…" : "Enviar fotos"}
          </button>
        </div>
        <div className="text-center text-[10px] text-slate-600 mt-4">INCONTI Corporation · Las fotos se comprimen automáticamente</div>
      </div>
    </div>
  );
}
